<script setup>
// ---------------------------------------------------------------------------
// The priciest contracts the Office has decided about.
//
// Rows, not decisions: this is a list of things that were bought, and two
// contracts under one decision are two contracts.
// ---------------------------------------------------------------------------

import { computed } from 'vue'

import { detailUrl, money, topContracts } from '../cases.js'
import { useContracts } from '../useCases.js'

const props = defineProps({
    src: { type: String, required: true },
    keep: { type: String, default: '20' },
    // Pipe-separated column headings, in the order the table draws them.
    columnLabels: { type: String, default: '' },
    // Pipe-separated: billions, millions, thousands.
    units: { type: String, default: '' },
    emptyText: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

const { rows, visible, failed } = useContracts(props.src)

const columns = computed(() => props.columnLabels.split('|'))

const contracts = computed(() =>
    rows.value.length ? topContracts(visible.value, Number(props.keep)) : []
)

/**
 * The file number, plus the decision id when this contract has two decisions
 * under the same one — thirteen file numbers in the source cover two decisions
 * each, and the links would otherwise read as a duplicate rather than as two
 * different documents.
 */
function label(contract, slot) {
    const number = contract.sp_zn[slot]
    const twice = contract.sp_zn.filter((other) => other === number).length > 1

    return twice ? `${number} (id:${contract.id[slot]})` : number
}

const billions = (value) => money(value, props.units.split('|'))
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <p v-else-if="rows.length && !contracts.length" class="chart__failed">{{ emptyText }}</p>

    <div v-else-if="contracts.length" class="datatable__scroll">
        <table class="table toplist">
            <thead>
                <tr>
                    <th v-for="column in columns" :key="column" scope="col">{{ column }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(contract, index) in contracts" :key="`${contract.vec}-${contract.cena}`">
                    <td class="toplist__rank">{{ index + 1 }}</td>
                    <td>
                        {{ contract.vec }}

                        <span class="toplist__cases">
                            <a
                                v-for="(id, slot) in contract.id"
                                :key="id"
                                :href="detailUrl(id)"
                                target="_blank"
                                rel="noopener"
                            >{{ label(contract, slot) }}</a>
                        </span>
                    </td>
                    <td>{{ contract.zadavatel ?? '—' }}</td>
                    <td class="datatable__num">{{ billions(contract.cena) }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
